import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MdCardMembership, MdClose, MdAdd, MdAutorenew, MdCheckCircle } from 'react-icons/md';
import * as planSvc from '../../services/membershipPlanService';
import useTheme from '../../context/ThemeContext';
import DualDatePicker from '../common/DualDatePicker';

const KTM = (5 * 60 + 45) * 60 * 1000; // Asia/Kathmandu
const pad2 = (n) => String(n).padStart(2, '0');
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtNPR = (n) => `NPR ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

const ktmYmd = (d) => { const k = new Date(new Date(d).getTime() + KTM); return { y: k.getUTCFullYear(), m: k.getUTCMonth() + 1, d: k.getUTCDate() }; };
const ktmTodayYmd = () => ktmYmd(new Date());
const ymdStr = ({ y, m, d }) => `${y}-${pad2(m)}-${pad2(d)}`;
const parseIso = (iso) => { const [y, m, d] = String(iso).split('-').map(Number); return { y, m, d }; };
const addMonthsYmd = ({ y, m, d }, months) => {
  const t = y * 12 + (m - 1) + months;
  const ny = Math.floor(t / 12); const nm = (t % 12) + 1;
  const dim = new Date(ny, nm, 0).getDate();
  return { y: ny, m: nm, d: Math.min(d, dim) };
};
const fmtYmd = ({ y, m, d }) => `${d} ${MONTHS[m - 1]} ${y}`;
const fmtDate = (instant) => fmtYmd(ktmYmd(instant));
const daysUntil = (instant) => Math.ceil((new Date(instant).getTime() - Date.now()) / 86400000);

// ── Assign / Renew modal ──────────────────────────────────────────────────────
function AssignModal({ studentId, plans, isRenewal, currentExpiry, onClose, onDone }) {
  const currentTheme = useTheme()?.currentTheme;
  const primaryBtn = currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card';
  const qc = useQueryClient();

  const [planId, setPlanId]   = useState(plans[0]?._id || '');
  const [startDate, setStartDate] = useState(ymdStr(ktmTodayYmd()));
  const [startTouched, setStartTouched] = useState(false);
  const [amount, setAmount]   = useState(plans[0]?.price ?? '');
  const [method, setMethod]   = useState('cash');
  const [notes, setNotes]     = useState('');
  const [submitting, setSubmitting] = useState(false);

  const selected = plans.find(p => p._id === planId);

  // Renewal extends from the current expiry when the member is still active and the start date
  // hasn't been touched — in that case we send NO startDate and the backend extends.
  const extendFromExpiry = isRenewal && !startTouched;
  const baseYmd = extendFromExpiry ? ktmYmd(currentExpiry) : parseIso(startDate);
  const projectedExpiry = selected ? fmtYmd(addMonthsYmd(baseYmd, selected.durationMonths)) : '—';

  const pickPlan = (p) => { setPlanId(p._id); setAmount(p.price); };

  const mutation = useMutation({
    mutationFn: () => planSvc.assignMembershipPlan({
      studentId, planId,
      method, amount: Number(amount),
      notes: notes.trim() || undefined,
      ...(extendFromExpiry ? {} : { startDate }),   // omit startDate → backend extends
    }),
    onSuccess: () => {
      toast.success(`Membership ${isRenewal ? 'renewed' : 'assigned'}`);
      // Prefix match: refreshes the open drawer's student-full query whatever its id key is.
      qc.invalidateQueries({ queryKey: ['student-full'] });
      qc.invalidateQueries({ queryKey: ['membership-plans'] });
      onDone();
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to assign plan'),
  });

  const submit = (e) => {
    e.preventDefault();
    if (!planId) return toast.error('Select a plan');
    if (!(Number(amount) >= 0)) return toast.error('Amount must be 0 or greater');
    setSubmitting(true);
    mutation.mutate(undefined, { onSettled: () => setSubmitting(false) });
  };

  const inp = 'w-full px-3 py-2.5 rounded-lg border text-sm outline-none transition-colors bg-nav-hover/40 border-divider focus:border-primary focus:ring-2 focus:ring-primary/30 text-secondary placeholder:text-subtle';
  const lbl = 'block text-xs font-semibold text-muted mb-1.5 uppercase tracking-wide';

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/40 sm:p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="bg-card border border-divider rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-divider flex-shrink-0">
          <h2 className="font-bold text-secondary flex items-center gap-2">
            <MdCardMembership className="text-primary" /> {isRenewal ? 'Renew Membership' : 'Assign Plan'}
          </h2>
          <button onClick={onClose} className="w-11 h-11 -mr-2 flex items-center justify-center rounded-lg text-muted hover:bg-nav-hover transition-colors">
            <MdClose className="text-lg" />
          </button>
        </div>

        <form onSubmit={submit} className="flex flex-col min-h-0 flex-1">
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4">
            {/* Plan selector */}
            <div>
              <label className={lbl}>Plan <span className="text-red-500">*</span></label>
              <div className="grid grid-cols-1 gap-2">
                {plans.map(p => {
                  const active = p._id === planId;
                  return (
                    <button type="button" key={p._id} onClick={() => pickPlan(p)}
                      className={`flex items-center justify-between gap-3 p-3 rounded-xl border text-left transition-colors ${
                        active ? 'border-primary bg-primary/10' : 'border-divider bg-nav-hover/30 hover:bg-nav-hover'
                      }`}>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-secondary truncate">{p.name}</p>
                        <p className="text-[12px] text-muted">{p.durationMonths} month{p.durationMonths === 1 ? '' : 's'}</p>
                      </div>
                      <span className="text-sm font-bold text-secondary flex-shrink-0">{fmtNPR(p.price)}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Start date */}
            <div>
              <label className={lbl}>Start date</label>
              <DualDatePicker value={startDate} onChange={(v) => { setStartDate(v); setStartTouched(true); }} />
              {extendFromExpiry && (
                <p className="text-[11px] text-muted mt-1.5">Renewals extend from the current expiry automatically — leave as-is unless starting on a specific date.</p>
              )}
            </div>

            {/* Amount + method */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={lbl}>Amount (NPR) <span className="text-red-500">*</span></label>
                <input className={inp} type="number" min="0" step="1" value={amount}
                  onChange={e => setAmount(e.target.value)} required />
              </div>
              <div>
                <label className={lbl}>Method <span className="text-red-500">*</span></label>
                <select className={inp} value={method} onChange={e => setMethod(e.target.value)}>
                  <option value="cash">Cash</option>
                  <option value="bank">Bank Transfer</option>
                  <option value="khalti">Khalti</option>
                  <option value="esewa">eSewa</option>
                  <option value="fonepay">Fonepay</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div>
              <label className={lbl}>Notes <span className="text-subtle normal-case font-normal">(optional)</span></label>
              <textarea className={inp} rows={2} value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Anything worth noting" />
            </div>

            {/* Confirm summary */}
            {selected && (
              <div className="rounded-xl bg-nav-hover/50 border border-divider px-3 py-2.5">
                <p className="text-[13px] text-secondary">
                  <span className="font-semibold">{selected.name}</span> · {fmtNPR(Number(amount) || 0)} · active until <span className="font-semibold">{projectedExpiry}</span>
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 px-5 py-3 border-t border-divider flex-shrink-0">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 rounded-full border border-divider text-secondary text-sm font-medium hover:bg-nav-hover transition-colors min-h-[44px]">
              Cancel
            </button>
            <button type="submit" disabled={submitting}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold ${primaryBtn} hover:opacity-90 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 min-h-[44px]`}>
              {submitting ? 'Saving…' : isRenewal ? 'Renew' : 'Assign Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────
export default function MembershipCard({ studentId, currentPlanId, currentPlanName, membershipExpiresAt }) {
  const currentTheme = useTheme()?.currentTheme;
  const primaryBtn = currentTheme === 'dark' ? 'bg-primary text-white' : 'bg-secondary text-card';
  const [modalOpen, setModalOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['membership-plans'],
    queryFn: () => planSvc.getMembershipPlans().then(r => r.data),
  });
  const activePlans = useMemo(() => (data?.plans || []).filter(p => p.isActive), [data]);

  const hasPlan = !!membershipExpiresAt;
  // Prefer the name resolved server-side (works for archived plans); fall back to the active
  // list, then a generic label.
  const planName = currentPlanName
    || (data?.plans || []).find(p => p._id === currentPlanId)?.name
    || 'Membership';

  // Status pill (Kathmandu time).
  let pill = null;
  if (hasPlan) {
    const left = daysUntil(membershipExpiresAt);
    if (left <= 0)      pill = { text: 'Expired', cls: 'bg-red-500/15 text-red-400 border-red-500/25' };
    else if (left <= 7) pill = { text: `${left} day${left === 1 ? '' : 's'} left`, cls: 'bg-amber-500/15 text-amber-400 border-amber-500/25' };
    else                pill = { text: 'Active', cls: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' };
  }

  const noPlans = activePlans.length === 0;

  return (
    <div className="bg-card border border-divider rounded-2xl p-4">
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="text-sm font-bold text-secondary flex items-center gap-2">
          <MdCardMembership className="text-primary text-base" /> Membership
        </p>
        {pill && <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${pill.cls}`}>{pill.text}</span>}
      </div>

      {hasPlan ? (
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-secondary truncate">{planName}</p>
            <p className="text-[13px] text-muted mt-0.5">
              {daysUntil(membershipExpiresAt) <= 0 ? 'Expired on ' : 'Active until '}
              <span className="font-medium text-secondary">{fmtDate(membershipExpiresAt)}</span>
            </p>
          </div>
          <button onClick={() => setModalOpen(true)} disabled={noPlans}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold ${primaryBtn} hover:opacity-90 active:scale-[0.98] transition-all duration-150 min-h-[40px] flex-shrink-0 disabled:opacity-50`}>
            <MdAutorenew className="text-base" /> Renew
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-3">
          <p className="text-[13px] text-muted">No active membership plan assigned yet.</p>
          <button onClick={() => setModalOpen(true)} disabled={noPlans}
            className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold ${primaryBtn} hover:opacity-90 active:scale-[0.98] transition-all duration-150 min-h-[40px] flex-shrink-0 disabled:opacity-50`}>
            <MdAdd className="text-base" /> Assign Plan
          </button>
        </div>
      )}

      {noPlans && (
        <p className="text-[11px] text-subtle mt-2 flex items-center gap-1">
          <MdCheckCircle className="text-[13px]" /> Create plans in Settings → Membership Plans first.
        </p>
      )}

      {modalOpen && !noPlans && (
        <AssignModal
          studentId={studentId}
          plans={activePlans}
          isRenewal={hasPlan && daysUntil(membershipExpiresAt) > 0}
          currentExpiry={membershipExpiresAt}
          onClose={() => setModalOpen(false)}
          onDone={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}
